<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { NxTooltip } from '../Tooltip';
import NxSpinner, { type NxSpinnerSize } from '../Loading/NxSpinner.vue';

export type NxButtonSize = 'xs' | 's' | 'm' | 'l' | 'xl';
export type NxButtonWidth = 'narrow' | 'normal' | 'full' | 'icon-only';
export type NxButtonVariant =
  | 'default'
  | 'outline'
  | 'ghost'
  | 'ghost-danger'
  | 'dashed'
  | 'danger'
  | 'outline-danger'
  | 'link';
/** Tone of the pressed look: `brand` (default) or `warning` — an „important“ flag. */
export type NxButtonPressedTone = 'brand' | 'warning';

export interface NxButtonProps {
  /** Defaults to `'normal'` */
  width?: NxButtonWidth;
  /** Defaults to `'s'` (icon-only buttons too) */
  height?: NxButtonSize;
  /**
   * Defaults to `'default'`. `link` reads as a text link — brand colour, no
   * border, fill or padding, underlined on hover — for an inline jump inside a
   * card or a line of text; the text size follows `height`.
   * `ghost-danger` rests like `ghost`, with a danger tint on hover and press
   * for destructive toolbar actions.
   */
  variant?: NxButtonVariant;
  /** Defaults to `'button'` */
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  /**
   * Opt-in busy state: a spinner is laid over the content (the button keeps
   * its size and its accessible name), `aria-busy` is set and clicks are
   * ignored — the button stays focusable, unlike `disabled`.
   */
  loading?: boolean;
  /**
   * Opt-in toggle state: sets `aria-pressed` and the kit's pressed look (brand
   * tint) — a ribbon toggle such as „Absatz zusammenhalten“. Unset leaves the
   * button a plain one (no `aria-pressed`).
   */
  pressed?: boolean;
  /**
   * Tone of the pressed look: `brand` (default) or `warning` — warning-subtle
   * fill, warning border and text, for a toggle that flags something as
   * important (the B-DMS „Wichtig“, #2376 K3). No effect while not pressed.
   */
  pressedTone?: NxButtonPressedTone;
  /**
   * Where the content sits: `center` (default) or `start` — a full-width
   * button that is a row of a list (a folder to pick), read from the start.
   */
  align?: 'center' | 'start';
  /**
   * Opt-in single line: the label ends in an ellipsis where the button runs out
   * of room (give it `max-width: 100%` room, e.g. as a flex item that may
   * shrink), and the kit tooltip shows the whole text — only while it is cut.
   * A leading icon in the slot stays before the text. Off by default.
   */
  truncate?: boolean;
}

const props = withDefaults(defineProps<NxButtonProps>(), {
  width: 'normal',
  height: undefined,
  variant: 'default',
  type: 'button',
  disabled: false,
  loading: false,
  pressed: undefined,
  pressedTone: 'brand',
  align: 'center',
  truncate: false,
});

const btnRef = useTemplateRef('btn-ref');

const _height = computed(() => props.height ?? 's');

/** The spinner takes the icon size an icon at this button height would have. */
const spinnerSize = computed<NxSpinnerSize>(() => {
  const iconOnly = props.width === 'icon-only';
  switch (_height.value) {
    case 'xs':
      return iconOnly ? 'sm' : 'xs';
    case 's':
      return iconOnly ? 'md' : 'sm';
    case 'l':
      return 'lg';
    case 'xl':
      return 'xl';
    default:
      return 'md';
  }
});

/**
 * Swallows clicks while loading. A capture listener on the button itself runs
 * before the consumer's `@click` (a fall-through listener on the same element)
 * and stops it; `preventDefault` also keeps a submit button from submitting.
 *
 * Added natively on purpose, not as `@click.capture`: a Vue listener stamps the
 * event, and Vue then drops the consumer's `@click` when the click comes in the
 * same millisecond the button was mounted (a test's mount-then-click).
 */
function onClickCapture(event: MouseEvent) {
  if (!props.loading) return;
  event.preventDefault();
  event.stopImmediatePropagation();
}
onMounted(() => btnRef.value?.addEventListener('click', onClickCapture, true));
onBeforeUnmount(() => btnRef.value?.removeEventListener('click', onClickCapture, true));

/** Hover delay before the full text of a cut label shows, like `NxClampedText`. */
const TRUNCATE_TIP_DELAY_MS = 300;

const labelRef = useTemplateRef<HTMLElement>('label-ref');
const tipOpen = ref(false);
const tipText = ref('');
let tipTimer: ReturnType<typeof setTimeout> | null = null;

function clearTipTimer() {
  if (tipTimer === null) return;
  clearTimeout(tipTimer);
  tipTimer = null;
}

/** Measured when the tooltip is about to open, so a resize needs no observer. */
function isCut(): boolean {
  const el = labelRef.value;
  return el !== null && el !== undefined && el.scrollWidth > el.clientWidth;
}

function armTip(delayMs: number) {
  if (!props.truncate) return;
  clearTipTimer();
  tipTimer = setTimeout(() => {
    tipTimer = null;
    if (!isCut()) return;
    tipText.value = labelRef.value?.textContent?.trim() ?? '';
    tipOpen.value = tipText.value !== '';
  }, delayMs);
}

function hideTip() {
  clearTipTimer();
  tipOpen.value = false;
}

/** A mouse click focuses the button too; only keyboard focus opens the tip. */
function onFocusIn(event: FocusEvent) {
  const target = event.target;
  try {
    if (target instanceof Element && !target.matches(':focus-visible')) return;
  } catch {
    /* no :focus-visible support — open */
  }
  armTip(TRUNCATE_TIP_DELAY_MS);
}
onBeforeUnmount(clearTipTimer);

defineExpose({
  focus: (options?: FocusOptions) => btnRef.value?.focus(options),
});
</script>

<template>
  <button
    ref="btn-ref"
    :type="props.type"
    :disabled="props.disabled"
    :aria-busy="props.loading || undefined"
    :aria-pressed="props.pressed === undefined ? undefined : props.pressed"
    :class="[
      `height-${_height} width-${props.width} variant-${props.variant}`,
      {
        'is-loading': props.loading,
        'is-pressed': props.pressed === true,
        'pressed-tone-warning': props.pressed === true && props.pressedTone === 'warning',
        'align-start': props.align === 'start',
        'is-truncate': props.truncate,
      },
    ]"
    @mouseenter="armTip(TRUNCATE_TIP_DELAY_MS)"
    @mouseleave="hideTip"
    @focusin="onFocusIn"
    @focusout="hideTip"
    @keydown.esc="hideTip"
  >
    <span v-if="props.loading" class="nx-button__spinner" data-testid="nx-button-spinner">
      <NxSpinner :size="spinnerSize" />
    </span>
    <span v-if="props.truncate" ref="label-ref" class="nx-button__label"><slot /></span>
    <slot v-else />
    <!-- Anchored mode: the bubble is teleported out, so nothing is added to the button's layout. -->
    <NxTooltip v-if="props.truncate" :anchor="labelRef" :open="tipOpen" :text="tipText" />
  </button>
</template>

<style scoped>
/* PILOT (I-555): NxButton consumes the new --nxt-button-* component tokens.
   The base rules read only the component's own tokens; each variant re-points
   those tokens at a role (brand / danger / transparent). Disabled is driven by
   dedicated tokens (no opacity hack) and focus shows a token-coloured ring. */
button {
  font-weight: var(--nxt-font-weight-medium);
  /* The control line, not the body's reading line: same height as an input field of this size. */
  line-height: var(--nxt-line-height-control);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  cursor: pointer;
  border-radius: var(--nxt-radius-md);
  border: var(--nxt-hairline) solid var(--nxt-button-border);
  padding-block: var(--nxt-space-xs);
  padding-inline: var(--nxt-space-sm);
  font-size: var(--nxt-button-size);
  color: var(--nxt-button-fg);
  background-color: var(--nxt-button-bg);
}
/* `align="start"`: content from the start, a wrapped label too. */
button.align-start {
  justify-content: flex-start;
  text-align: start;
}
button:hover:not(:disabled) {
  background-color: var(--nxt-button-bg-hover);
  border-color: var(--nxt-button-border-hover);
  color: var(--nxt-button-fg-hover);
}
button:active:not(:disabled) {
  background-color: var(--nxt-button-bg-active);
  border-color: var(--nxt-button-border-active);
}
button:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-button-ring);
  outline-offset: calc(var(--nxt-size-unit) * 0.125);
}
button:disabled {
  cursor: not-allowed;
  background-color: var(--nxt-button-bg-disabled);
  color: var(--nxt-button-fg-disabled);
  /* Outlined variants keep their border shape when disabled; solids fall back
     to the disabled fill so border == bg. */
  border-color: var(--nxt-button-border-disabled, var(--nxt-button-bg-disabled));
}

/* Loading: the content keeps its place (and its accessible name) but turns
   invisible; the spinner sits centred over it in the button's text colour. */
button.is-loading {
  position: relative;
  cursor: progress;
  color: transparent;

  &:hover:not(:disabled) {
    color: transparent;
  }

  & > :not(.nx-button__spinner) {
    opacity: 0;
  }
}
.nx-button__spinner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-button-fg);
  opacity: 1;
}
button.width-icon-only > .nx-button__spinner {
  /* the icon-only centring rule below must not shift the full-size overlay */
  top: 0;
  left: 0;
  transform: none;
}

/* `truncate`: the label is the one part that shrinks and ends in an ellipsis. */
button.is-truncate {
  max-width: 100%;
  min-width: 0;
}
.nx-button__label {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* a leading icon is part of the label span: keep the button's gap after it */
  & :deep(svg) {
    vertical-align: middle;
    margin-inline-end: var(--nxt-space-xs);
  }
}

/* Sizes map to the button-size token (base tokens already carry --nxt-font-scale). */
button.height-xs {
  --nxt-button-size: var(--nxt-font-size-xs);
}
button.height-s {
  --nxt-button-size: var(--nxt-font-size-sm);
}
button.height-m {
  --nxt-button-size: var(--nxt-font-size-md);
}
button.height-l {
  --nxt-button-size: var(--nxt-font-size-lg);
}
button.height-xl {
  --nxt-button-size: var(--nxt-font-size-xl);
}

button.width-narrow {
  padding-inline: var(--nxt-space-xs);
}
button.width-full {
  width: 100%;
}
button.width-icon-only {
  /* same height as a text button of its size: line-height + 2 * padding-block
     + its border, so icon and text buttons in one toolbar line up (I-2510) */
  height: calc(1lh + 2 * var(--nxt-space-xs) + 2 * var(--nxt-hairline));
  /* width = height */
  width: calc(1lh + 2 * var(--nxt-space-xs) + 2 * var(--nxt-hairline));
  position: relative;

  & > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

/* variant-default consumes the :root defaults (brand); only pin the resting
   border to the fill so the solid button has no contrasting ring. */
button.variant-default {
  --nxt-button-border: var(--nxt-button-bg);
}
/* Secondary buttons are NEUTRAL, not brand (§3 60-30-10): reserve the brand
   colour for the one primary action (variant-default) + active/hover states.
   Resting text/border are neutral; hover is a soft neutral fill. */
button.variant-outline {
  --nxt-button-bg: transparent;
  --nxt-button-fg: var(--nxt-fg);
  --nxt-button-border: var(--nxt-border);
  --nxt-button-bg-hover: var(--nxt-surface-sunken-bg);
  --nxt-button-fg-hover: var(--nxt-fg);
  --nxt-button-border-hover: var(--nxt-border-strong);
  --nxt-button-bg-active: var(--nxt-neutral-subtle-bg-hover);
  --nxt-button-border-active: var(--nxt-border-strong);
  --nxt-button-bg-disabled: transparent;
  --nxt-button-border-disabled: var(--nxt-border);
}
button.variant-ghost,
button.variant-ghost-danger {
  --nxt-button-bg: transparent;
  --nxt-button-fg: var(--nxt-fg);
  --nxt-button-border: transparent;
  --nxt-button-bg-hover: var(--nxt-surface-sunken-bg);
  --nxt-button-fg-hover: var(--nxt-fg);
  --nxt-button-border-hover: transparent;
  --nxt-button-bg-active: var(--nxt-neutral-subtle-bg-hover);
  --nxt-button-border-active: transparent;
  --nxt-button-bg-disabled: transparent;
}
button.variant-ghost-danger {
  --nxt-button-bg-hover: var(--nxt-danger-subtle-bg);
  --nxt-button-fg-hover: var(--nxt-danger-subtle-fg);
  --nxt-button-bg-active: var(--nxt-danger-subtle-bg-hover);
  --nxt-button-ring: var(--nxt-danger-bg);
}
button.variant-dashed {
  /* Rest + hover: transparent bg, only the dashed border colour changes.
     Active + fg: exactly like outline (subtle fill + strong border, subtle-fg). */
  --nxt-button-bg: transparent;
  --nxt-button-bg-hover: transparent;
  --nxt-button-bg-active: var(--nxt-brand-subtle-bg-hover);
  --nxt-button-fg: var(--nxt-brand-bg);
  --nxt-button-fg-hover: var(--nxt-brand-subtle-fg);
  --nxt-button-border: var(--nxt-border-strong);
  --nxt-button-border-hover: var(--nxt-brand-bg);
  --nxt-button-border-active: var(--nxt-border-strong);
  --nxt-button-bg-disabled: transparent;
  --nxt-button-border-disabled: var(--nxt-border-strong);
  border-style: dashed;
}
button.variant-danger {
  --nxt-button-bg: var(--nxt-danger-bg);
  --nxt-button-fg: var(--nxt-danger-fg);
  --nxt-button-border: var(--nxt-danger-bg);
  --nxt-button-bg-hover: var(--nxt-danger-bg-hover);
  --nxt-button-border-hover: var(--nxt-danger-bg-hover);
  --nxt-button-bg-active: var(--nxt-danger-bg-active);
  --nxt-button-border-active: var(--nxt-danger-bg-active);
  --nxt-button-ring: var(--nxt-danger-bg);
}
button.variant-outline-danger {
  --nxt-button-bg: transparent;
  --nxt-button-fg: var(--nxt-danger-bg);
  --nxt-button-border: var(--nxt-danger-border);
  --nxt-button-bg-hover: var(--nxt-danger-subtle-bg);
  --nxt-button-fg-hover: var(--nxt-danger-subtle-fg);
  --nxt-button-border-hover: var(--nxt-danger-border);
  --nxt-button-bg-active: var(--nxt-danger-subtle-bg-hover);
  --nxt-button-border-active: var(--nxt-danger-border);
  --nxt-button-ring: var(--nxt-danger-bg);
  --nxt-button-bg-disabled: transparent;
  /* disabled border stays neutral, not red */
  --nxt-button-border-disabled: var(--nxt-border);
}

/* A text link: brand text, nothing around it. Hover underlines instead of
   filling; the size comes from `height` like every variant, the box is just the
   text (no padding, no border) so it sits in a card line or a sentence. */
button.variant-link {
  --nxt-button-bg: transparent;
  --nxt-button-fg: var(--nxt-brand-bg);
  --nxt-button-border: transparent;
  --nxt-button-bg-hover: transparent;
  --nxt-button-fg-hover: var(--nxt-brand-bg-hover);
  --nxt-button-border-hover: transparent;
  --nxt-button-bg-active: transparent;
  --nxt-button-border-active: transparent;
  --nxt-button-bg-disabled: transparent;
  --nxt-button-border-disabled: transparent;
  padding: 0;
  border-radius: var(--nxt-radius-sm);
  font-weight: inherit;
  line-height: var(--nxt-line-height-normal);
  text-align: start;
}
button.variant-link:hover:not(:disabled) {
  text-decoration: underline;
}

/* Pressed toggle (`pressed`): the brand tint reads in both themes; hover keeps
   it. After the variants, which set the same tokens with the same specificity. */
button.is-pressed {
  --nxt-button-bg: var(--nxt-brand-subtle-bg);
  --nxt-button-fg: var(--nxt-brand-subtle-fg);
  --nxt-button-border: transparent;
  --nxt-button-bg-hover: var(--nxt-brand-subtle-bg-hover);
  --nxt-button-fg-hover: var(--nxt-brand-subtle-fg);
  --nxt-button-bg-active: var(--nxt-brand-subtle-bg-hover);
}
/* `pressedTone="warning"`: the warning pair instead of the brand tint, with a
   visible border, so an „important“ flag stands out in both themes. */
button.is-pressed.pressed-tone-warning {
  --nxt-button-bg: var(--nxt-warning-subtle-bg);
  --nxt-button-fg: var(--nxt-warning-bg);
  --nxt-button-border: var(--nxt-warning-border);
  --nxt-button-border-hover: var(--nxt-warning-border);
  --nxt-button-border-active: var(--nxt-warning-border);
  --nxt-button-bg-hover: var(--nxt-warning-subtle-bg-hover);
  --nxt-button-fg-hover: var(--nxt-warning-bg);
  --nxt-button-bg-active: var(--nxt-warning-subtle-bg-hover);
}
</style>
